// `/auth/passkeys`: the WebAuthn ceremonies and the status read (#636, D10).
//
// Read off `control_plane/routes/auth.py`. `POST /auth/passkeys/authenticate/begin`
// mints the challenge a step-up assertion answers, and `/auth/step-up` then takes
// `method: "passkey"` with a JSON credential carrying the ceremony token and the
// browser's response. `GET /auth/mfa` is where a signed-in session learns whether
// it has a passkey to offer at all.
//
// **A ceremony is two calls, in C3's shape** (`api/policy.ts`): begin mints the
// challenge and remembers it; the assertion proves it and mints the grant. The
// ceremony token is the tie between them, so a challenge nobody answered is
// worth nothing.

import type { Api } from "./http";
import { STEP_UP_PATH, type StepUp } from "./policy";

export const PASSKEYS_PATH = "/auth/passkeys";
export const PASSKEY_AUTHENTICATE_BEGIN_PATH = "/auth/passkeys/authenticate/begin";
export const PASSKEY_LOGIN_BEGIN_PATH = "/auth/passkeys/login/begin";
export const MFA_PATH = "/auth/mfa";
export const PASSKEY_REGISTER_BEGIN_PATH = "/auth/passkeys/register/begin";
export const PASSKEY_REGISTER_FINISH_PATH = "/auth/passkeys/register/finish";
/** `auth/service.py::PASSKEY_REGISTER_PURPOSE`: the step-up an enrolment rides (#658). */
export const PASSKEY_REGISTER_PURPOSE = "passkey_register";
/** `auth/service.py::PASSKEY_REMOVE_PURPOSE`: the step-up a removal rides (#658). */
export const PASSKEY_REMOVE_PURPOSE = "passkey_remove";

/** One enrolled credential, as the list route shows it. Never the public key. */
export interface PasskeyRow {
  credential_id: string;
  name: string;
  transports: string[];
  backed_up: boolean;
  created_at: string;
  last_used_at: string | null;
}

/** The server's assertion options: base64url where the DOM wants a buffer. */
export interface PasskeyChallenge {
  ceremony_token: string;
  public_key: {
    challenge: string;
    rpId: string;
    timeout?: number;
    userVerification?: string;
    allowCredentials?: PasskeyDescriptor[];
  };
}

/** A credential the browser should not create again, or may answer with. */
export interface PasskeyDescriptor {
  type: string;
  id: string;
  transports?: string[];
}

/** The server's registration options: base64url where the DOM wants a buffer. */
export interface PasskeyCreationChallenge {
  ceremony_token: string;
  public_key: {
    challenge: string;
    rp: { id: string; name: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: { type: string; alg: number }[];
    timeout?: number;
    attestation?: string;
    authenticatorSelection?: { residentKey?: string; userVerification?: string };
    excludeCredentials?: PasskeyDescriptor[];
  };
}

/** The create prompt's answer as the server's JSON: base64url strings, never buffers. */
export interface AttestationJson {
  id: string;
  rawId: string;
  type: string;
  response: { clientDataJSON: string; attestationObject: string };
  transports: string[];
}

/** What `GET /auth/mfa` says about the factors this session holds. */
export interface MfaStatus {
  /** Whether the user has a password to step up with at all (#659). */
  password_set: boolean;
  mfa_enabled: boolean;
  totp_enabled: boolean;
  passkeys_enabled: boolean;
  passkeys: number;
  /** Unused recovery codes; answered only while TOTP is enrolled. */
  recovery_codes_remaining?: number;
}

/** The step-up methods a user holds, as the step-up dialog reads them (#659). */
export interface StepUpFactors {
  passkey: boolean;
  totp: boolean;
  password: boolean;
  /** An unused recovery code, the TOTP holder's fallback (#729); absent is none. */
  recovery?: boolean;
}

export const stepUpFactorsOf = (status: MfaStatus): StepUpFactors => ({
  passkey: status.passkeys_enabled,
  totp: status.totp_enabled,
  password: status.password_set,
  recovery: status.totp_enabled && (status.recovery_codes_remaining ?? 0) > 0,
});

export const readMfaStatus = (api: Api): Promise<MfaStatus> =>
  api.get<MfaStatus>(MFA_PATH);

/** What a step-up site offers: `GET /auth/mfa`, read as factors. */
export const readStepUpFactors = async (api: Api): Promise<StepUpFactors> =>
  stepUpFactorsOf(await readMfaStatus(api));

export const listPasskeys = (api: Api): Promise<PasskeyRow[]> =>
  api.get<PasskeyRow[]>(PASSKEYS_PATH);

/** Mint an assertion challenge bound to one purpose; the ceremony token ties the answer. */
export const beginPasskeyAuthentication = (
  api: Api,
  purpose: string,
): Promise<PasskeyChallenge> =>
  api.post<PasskeyChallenge>(PASSKEY_AUTHENTICATE_BEGIN_PATH, { purpose });

/** Mint the assertion challenge that answers a login's `mfa_token`. */
export const beginPasskeyLogin = (
  api: Api,
  mfaToken: string,
): Promise<PasskeyChallenge> =>
  api.post<PasskeyChallenge>(PASSKEY_LOGIN_BEGIN_PATH, { mfa_token: mfaToken });

/** Mint the registration options a signed-in user's create prompt answers (#660). */
export const beginPasskeyRegistration = (api: Api): Promise<PasskeyCreationChallenge> =>
  api.post<PasskeyCreationChallenge>(PASSKEY_REGISTER_BEGIN_PATH);

/** What `POST /auth/passkeys/register/finish` takes: the answer, a name, and the grant. */
export interface PasskeyRegistration {
  ceremony_token: string;
  credential: AttestationJson;
  name: string;
  /** From a step-up for `PASSKEY_REGISTER_PURPOSE`; null for a user with nothing to step up with. */
  step_up_grant: string | null;
}

export const finishPasskeyRegistration = (
  api: Api,
  registration: PasskeyRegistration,
): Promise<{ credential_id: string; name: string }> =>
  api.post<{ credential_id: string; name: string }>(PASSKEY_REGISTER_FINISH_PATH, registration);

/**
 * Remove one of the user's own passkeys (#661). The grant rides the body; a
 * passkey that is not the user's, or is already gone, answers 404.
 */
export const removePasskey = (api: Api, credentialId: string, grant: string): Promise<void> =>
  api.delete<void>(`${PASSKEYS_PATH}/${encodeURIComponent(credentialId)}`, {
    step_up_grant: grant,
  });

/** `POST /auth/step-up` for one purpose; the grant it answers with, as a string. */
export async function mintStepUpGrant(api: Api, proof: StepUp, purpose: string): Promise<string> {
  const granted = await api.post<{ grant?: unknown }>(STEP_UP_PATH, {
    method: proof.method,
    credential: proof.credential,
    purpose,
  });
  return typeof granted?.grant === "string" ? granted.grant : "";
}

/**
 * The passkey calls Settings makes (#660), bound to one `Api`: the console
 * builds it over its own client, and a test over a fake one, so the screen is
 * tested against the real request shapes.
 */
export interface PasskeyActions {
  readMfa(): Promise<MfaStatus>;
  list(): Promise<PasskeyRow[]>;
  /** The assertion challenge a passkey step-up for `purpose` answers. */
  beginPasskey(purpose: string): Promise<PasskeyChallenge>;
  stepUp(proof: StepUp, purpose: string): Promise<string>;
  beginRegistration(): Promise<PasskeyCreationChallenge>;
  finishRegistration(registration: PasskeyRegistration): Promise<{ credential_id: string; name: string }>;
  remove(credentialId: string, grant: string): Promise<void>;
}

export const passkeyActions = (api: Api): PasskeyActions => ({
  readMfa: () => readMfaStatus(api),
  list: () => listPasskeys(api),
  beginPasskey: (purpose) => beginPasskeyAuthentication(api, purpose),
  stepUp: (proof, purpose) => mintStepUpGrant(api, proof, purpose),
  beginRegistration: () => beginPasskeyRegistration(api),
  finishRegistration: (registration) => finishPasskeyRegistration(api, registration),
  remove: (credentialId, grant) => removePasskey(api, credentialId, grant),
});
